import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { Campaign, Targetable, Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import {
  determineInterestsMatchMode,
  getAgeCategoryIds,
  getGeoFilterIds,
  getGeoTargetingsArray,
  getInterestIds,
  getNonArchivedTargetings,
} from '@feathr/extender/components/Targetings';
import { useStore } from '@feathr/extender/state';
import { useDebouncedCallback } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

interface IForecastPayload {
  included_geo_filter_ids?: string[];
  excluded_geo_filter_ids?: string[];
  datagroup_data: Array<{
    interest_ids?: string[];
    age_category_ids?: string[];
  }>;
}

interface IUseEstimatedReachProps {
  targetings: ListResponse<Targeting>;
  campaign: Campaign;
}

interface IUseEstimatedReach {
  isLoading: boolean;
  estimatedReach: number | null;
}

/**
 * Hook to fetch audience breakdown data for targets step.
 * This can be used in the parent component and shared with child components.
 */
function useEstimatedReach({
  targetings,
  campaign,
}: IUseEstimatedReachProps): Readonly<IUseEstimatedReach> {
  const { t } = useTranslation();
  const { Segments, Targetables } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedReach, setEstimatedReach] = useState<number | null>(null);
  const { isOmniReachCampaign } = campaign;
  const abortControllerRef = useRef<AbortController | null>(null);
  const interestsCacheRef = useRef<Targetable[] | null>(null);

  // Omni campaign is included in segment campaign
  const shouldFetchEstimatedReach = isOmniReachCampaign && !targetings.isPending;

  async function buildForecastPayload(
    targetings: ListResponse<Targeting>,
  ): Promise<IForecastPayload> {
    const targetingsArray = targetings.models;

    const { included: includedGeoFilterIds, excluded: excludedGeoFilterIds } =
      await getGeoFilterIds(targetingsArray, Targetables);

    if (!interestsCacheRef.current) {
      const { targetables } = await Targetables.getInterests();
      interestsCacheRef.current = targetables;
    }

    const ageCategoryIds = await getAgeCategoryIds(targetingsArray, Targetables);
    const { interestIds, matchMode } = getInterestIds(targetingsArray, interestsCacheRef.current);

    // Add to an array to be spread and to avoid adding passing an empty array of interest_ids
    const interestsPayload =
      matchMode === 'match_any'
        ? [{ interest_ids: interestIds }]
        : interestIds.map((id) => ({ interest_ids: [id] }));

    // Add to an array to be spread and to avoid adding passing an empty array of age_category_ids
    const ageCategoryPayload = [{ age_category_ids: ageCategoryIds }];

    const allAgeDemographicsIds = Targetables.getAgeDemographics()
      .models.map((t) => t.get('age_category'))
      .filter((ageCategory): ageCategory is string => typeof ageCategory === 'string');

    const hasOnlyLocationTargetings =
      interestIds.length === 0 &&
      ageCategoryIds.length === 0 &&
      (includedGeoFilterIds.length > 0 || excludedGeoFilterIds.length > 0);

    const locationOnlyAgeDemographics = hasOnlyLocationTargetings
      ? [{ age_category_ids: allAgeDemographicsIds }]
      : [];

    return {
      ...(includedGeoFilterIds.length > 0 && { included_geo_filter_ids: includedGeoFilterIds }),
      ...(excludedGeoFilterIds.length > 0 && { excluded_geo_filter_ids: excludedGeoFilterIds }),
      datagroup_data: [
        ...(interestIds.length > 0 ? interestsPayload : []),
        // If only locations have been passed, force all available age demographics because the
        // forecast endpoint requires some data ids
        ...(ageCategoryIds.length > 0 ? ageCategoryPayload : locationOnlyAgeDemographics),
      ],
    };
  }

  // We added or removed a targeting, refetch the estimated reach
  const numTargetings = getNonArchivedTargetings(targetings.models).length;
  // We changed the interests match mode, refetch the estimated reach
  const interestsMatchMode = determineInterestsMatchMode(targetings.models);
  // We changed a geo targeting location or inclusion/exclusion, refetch the estimated reach
  const geoTargetings = JSON.stringify(getGeoTargetingsArray(targetings.models));

  // Create the fetch function with useCallback to make it stable
  const fetchEstimatedReach = useCallback(async (): Promise<void> => {
    // Don't fetch if targetings are still loading
    if (targetings.isPending) {
      setIsLoading(false);
      return;
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Build payload and wait for all targetables to load
      const payload = await buildForecastPayload(targetings);
      const response = await Segments.getForecast(payload, abortController.signal);

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setEstimatedReach(response.min ?? 0);
      }
    } catch (error) {
      // Don't show error toast if the request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        toast(
          t("We couldn't fetch estimated reach. Try again later.", {
            error,
          }),
          {
            type: ToastType.ERROR,
          },
        );
        setEstimatedReach(null);
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [targetings, Segments, Targetables, t]);

  // Debounce the fetch function - it will wait 1000ms after the last change
  const debouncedFetchEstimatedReach = useDebouncedCallback(fetchEstimatedReach, 1000, [
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
  ]);

  // Set loading immediately when dependencies change (before debounce)
  useEffect(() => {
    if (shouldFetchEstimatedReach) {
      setIsLoading(true);
    }
  }, [
    shouldFetchEstimatedReach,
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
  ]);

  // Fetch the estimated reach when dependencies change (debounced)
  useEffect(() => {
    // Only try to get the breakdown if it's an omni/retargeting campaign with included segments
    if (shouldFetchEstimatedReach) {
      debouncedFetchEstimatedReach();
    }

    // Cleanup: abort any in-flight request when effect is cleaned up
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    shouldFetchEstimatedReach,
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
    debouncedFetchEstimatedReach,
  ]);

  return {
    isLoading: isLoading || targetings.isPending,
    estimatedReach,
  };
}

export default useEstimatedReach;
